/**
 * @fileoverview Input/output schemas for the plot-extractor-v2 agent.
 *
 * This agent extracts a summary and writtenPlot from unstructured text.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  textInput: z.string().describe('The full text content of the document to analyze.'),
  scopeGuidance: z.string().optional().describe('Scope guidance for writtenPlot length, computed from projected word count.'),
  storyType: z.string().optional().describe('The story type (e.g., "Novelette", "Novel", "Epic"). Used to calibrate writtenPlot detail level.'),
  projectedWordCount: z.number().optional().describe('The target word count for the story. Used to calibrate writtenPlot detail level.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

// Note: Character extraction is handled by character-extractor-full-v2, not this agent.
export const OutputSchema = z.object({
  summary: z.string().describe("A concise overview of the plot (100-150 words) that weaves together the story summary, its central theme, and the main conflict."),
  writtenPlot: z.string().describe(
    'A detailed, multi-paragraph plot outline in Markdown format. ' +
    'Use ## headings for each structural phase (Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State). ' +
    'Use **bold** for character names and key events, *italic* for emotional states. ' +
    'Separate paragraphs with double newlines. Do NOT use HTML tags. ' +
    'Weave together all storylines into a cohesive narrative covering every phase. ' +
    'Write in present tense, third person. ' +
    'The narrative should be verbose and granular enough to guide subsequent chapter planning and prose drafting.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
